import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Download, Printer, QrCode, Info, LogIn, LogOut, RefreshCw } from 'lucide-react';
import { barcodeAPI } from '../api/apiService';
import './MyBarcode.css';

export default function MyBarcode() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const printRef = useRef(null);

  const load = () => {
    setLoading(true);
    setError('');
    barcodeAPI.myBarcode()
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load barcode'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>My Barcode — ${data?.name}</title>
      <style>
        body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
        h2  { font-size: 22px; margin-bottom: 6px; }
        p   { color: #555; font-size: 14px; margin: 4px 0; }
        .code { font-family: monospace; font-size: 15px; margin-top: 16px;
                padding: 10px 14px; background: #f3f4f6; border-radius: 8px;
                display: inline-block; letter-spacing: 0.04em; }
      </style></head>
      <body>
        <h2>${data?.name}</h2>
        <p>Employee ID: ${data?.employee_id}</p>
        <div style="margin: 24px 0; display:inline-block;">${content.innerHTML}</div>
        <div class="code">${data?.barcode}</div>
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const handleDownload = () => {
    const svg = printRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas  = document.createElement('canvas');
    canvas.width  = 300;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      const link    = document.createElement('a');
      link.download = `barcode-${data?.name?.replace(/\s+/g, '-') ?? 'employee'}.png`;
      link.href     = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // ── Loading state ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="mb-page">
        <div className="mb-loading">
          <div className="mb-spinner" />
          <p>Loading your barcode…</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────
  if (error) {
    return (
      <div className="mb-page">
        <div className="mb-error-card">
          <QrCode size={40} />
          <p>{error}</p>
          <button className="mb-btn-primary" onClick={load}>
            <RefreshCw size={14} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Today's status helpers ─────────────────────────────────
  const todayScans   = data?.today_scans ?? [];
  const lastScan     = todayScans.at(-1);
  const isCheckedIn  = lastScan?.scan_type === 'CHECKIN';
  const checkInScan  = todayScans.find(s => s.scan_type === 'CHECKIN');
  const checkOutScan = [...todayScans].reverse().find(s => s.scan_type === 'CHECKOUT');

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="mb-page">

      {/* Header */}
      <div className="mb-header">
        <div className="mb-header-icon"><QrCode size={20} /></div>
        <div>
          <h1 className="mb-title">My Barcode</h1>
          <p className="mb-subtitle">Show this QR code at the entrance to check in or check out</p>
        </div>
      </div>

      <div className="mb-grid">

        {/* ── QR Card ── */}
        <div className="mb-card mb-qr-card">
          <div className="mb-employee-info">
            <div className="mb-avatar">{(data?.name || 'U')[0].toUpperCase()}</div>
            <div>
              <p className="mb-emp-name">{data?.name}</p>
              <p className="mb-emp-id">Employee ID: {data?.employee_id}</p>
            </div>
          </div>

          {/* QR Code — white background required for scanning */}
          <div ref={printRef} className="mb-qr-wrapper">
            {data?.barcode ? (
              <QRCode
                value={data.barcode}
                size={220}
                style={{ height: 'auto', maxWidth: '100%' }}
                viewBox="0 0 256 256"
              />
            ) : (
              <div className="mb-qr-placeholder">
                <QrCode size={56} />
                <p>No barcode generated yet</p>
              </div>
            )}
          </div>

          <div className="mb-barcode-id">
            <span className="mb-barcode-label">Barcode ID</span>
            <code className="mb-barcode-val">{data?.barcode}</code>
          </div>

          <div className="mb-actions">
            <button className="mb-btn-secondary" onClick={handlePrint}>
              <Printer size={14} /> Print
            </button>
            <button className="mb-btn-primary" onClick={handleDownload}>
              <Download size={14} /> Download PNG
            </button>
          </div>
        </div>

        {/* ── Today's Status ── */}
        <div className="mb-side">

          <div className="mb-card mb-status-card">
            <h2 className="mb-section-title">Today's Status</h2>

            {todayScans.length === 0 ? (
              <div className="mb-no-scans">
                <QrCode size={32} />
                <p>No scans recorded today</p>
              </div>
            ) : (
              <div className="mb-status-content">
                <div className={`mb-status-badge ${isCheckedIn ? 'mb-status-in' : 'mb-status-out'}`}>
                  {isCheckedIn ? (
                    <><LogIn size={16} /> Currently Checked In</>
                  ) : (
                    <><LogOut size={16} /> Checked Out</>
                  )}
                </div>

                <div className="mb-time-row">
                  {checkInScan && (
                    <div className="mb-time-box">
                      <span className="mb-time-label">Check-in</span>
                      <span className="mb-time-val mb-time-in">{fmtTime(checkInScan.scan_time)}</span>
                    </div>
                  )}
                  {checkOutScan && (
                    <div className="mb-time-box">
                      <span className="mb-time-label">Check-out</span>
                      <span className="mb-time-val mb-time-out">{fmtTime(checkOutScan.scan_time)}</span>
                    </div>
                  )}
                </div>

                <div className="mb-scan-list">
                  {todayScans.map((s, i) => (
                    <div key={i} className="mb-scan-row">
                      <span className={`mb-scan-dot ${s.scan_type === 'CHECKIN' ? 'mb-dot-in' : 'mb-dot-out'}`} />
                      <span className="mb-scan-type">{s.scan_type}</span>
                      <span className="mb-scan-ts">{fmtTime(s.scan_time)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* How to use */}
          <div className="mb-card mb-info-card">
            <div className="mb-info-icon"><Info size={16} /></div>
            <div>
              <p className="mb-info-title">How to use your barcode</p>
              <ul className="mb-info-list">
                <li>Show this QR code at the scanner kiosk</li>
                <li>First scan of the day = Check-in</li>
                <li>Next scan = Check-out (after 15 min)</li>
                <li>Staff can also scan your physical badge</li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
